import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { validateCNPJ, validateEmail, getValidationMessage } from "@/utils/validators";
import ValidationMessage from "./ValidationMessage";
import DuplicateWarning from "./DuplicateWarning";

interface CompanyStepProps {
  email: string;
  cnpj: string;
  companyName: string;
  partnerCode?: string;
  onChangeEmail: (value: string) => void;
  onChangeCnpj: (value: string) => void;
  onChangeCompanyName: (value: string) => void;
  onChangePartnerCode?: (value: string) => void;
  onBack: () => void;
  onContinue: () => void;
  emailDuplicate?: boolean;
  cnpjDuplicate?: boolean;
  onCheckDuplicate?: (field: "email" | "cnpj", value: string) => void;
}

const formatCnpjDisplay = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 14);
  if (digits.length === 0) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8)
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12)
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const CompanyStep = ({
  email,
  cnpj,
  companyName,
  partnerCode,
  onChangeEmail,
  onChangeCnpj,
  onChangeCompanyName,
  onChangePartnerCode,
  onBack,
  onContinue,
  emailDuplicate,
  cnpjDuplicate,
  onCheckDuplicate,
}: CompanyStepProps) => {
  const [cnpjDisplay, setCnpjDisplay] = useState(() => formatCnpjDisplay(cnpj));
  const [touched, setTouched] = useState({ email: false, cnpj: false, company: false });

  useEffect(() => {
    setCnpjDisplay(formatCnpjDisplay(cnpj));
  }, [cnpj]);

  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCnpjDisplay(e.target.value);
    setCnpjDisplay(formatted);
    const digits = e.target.value.replace(/\D/g, "").slice(0, 14);
    onChangeCnpj(digits);
    if (digits.length === 14) {
      onCheckDuplicate?.("cnpj", digits);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChangeEmail(val);
    if (val.includes("@") && val.length > 5) {
      onCheckDuplicate?.("email", val);
    }
  };

  const isEmailValid = validateEmail(email);
  const isCnpjValid = validateCNPJ(cnpj);
  const isCompanyValid = companyName.trim().length >= 2;
  const canContinue = isEmailValid && isCnpjValid && isCompanyValid && !emailDuplicate && !cnpjDuplicate;

  const emailMsg = touched.email ? getValidationMessage("email", email) : null;
  const cnpjMsg = touched.cnpj ? getValidationMessage("cnpj", cnpj) : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-8"
    >
      <div className="text-center space-y-2">
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
          Conta mais sobre seu negócio
        </h2>
        <p className="text-muted-foreground text-base">
          Informações da sua empresa
        </p>
      </div>

      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-foreground">
            E-mail
          </Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            placeholder="seunome@empresa.com.br"
            className="h-14 rounded-xl border-border/50 bg-accent/30 focus:bg-background focus:border-primary transition-all text-base"
          />
          {emailDuplicate && <DuplicateWarning field="e-mail" />}
          {emailMsg ? (
            <ValidationMessage message={emailMsg} />
          ) : (
            touched.email && isEmailValid && !emailDuplicate && <ValidationMessage message={null} isValid />
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="cnpj" className="text-sm font-medium text-foreground">
            CNPJ
          </Label>
          <Input
            id="cnpj"
            value={cnpjDisplay}
            onChange={handleCnpjChange}
            onBlur={() => setTouched((t) => ({ ...t, cnpj: true }))}
            placeholder="00.000.000/0000-00"
            className="h-14 rounded-xl border-border/50 bg-accent/30 focus:bg-background focus:border-primary transition-all text-base"
          />
          {cnpjDuplicate && <DuplicateWarning field="CNPJ" />}
          {cnpjMsg ? (
            <ValidationMessage message={cnpjMsg} />
          ) : (
            touched.cnpj && isCnpjValid && !cnpjDuplicate && <ValidationMessage message={null} isValid />
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="companyName" className="text-sm font-medium text-foreground">
            Nome da Empresa
          </Label>
          <Input
            id="companyName"
            value={companyName}
            onChange={(e) => onChangeCompanyName(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, company: true }))}
            placeholder="Digite o nome da sua empresa"
            className="h-14 rounded-xl border-border/50 bg-accent/30 focus:bg-background focus:border-primary transition-all text-base"
          />
          {touched.company && !isCompanyValid && companyName.length > 0 && (
            <ValidationMessage message="Nome da empresa deve ter pelo menos 2 caracteres" />
          )}
        </div>

        {onChangePartnerCode && (
          <div className="space-y-2">
            <Label htmlFor="partnerCode" className="text-sm font-medium text-foreground">
              Código da Parceira <span className="text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <Input
              id="partnerCode"
              value={partnerCode || ""}
              onChange={(e) => onChangePartnerCode(e.target.value.toUpperCase().trim())}
              placeholder="Ex.: PARCEIRA123"
              className="h-14 rounded-xl border-border/50 bg-accent/30 focus:bg-background focus:border-primary transition-all text-base uppercase"
            />
            <p className="text-xs text-muted-foreground">
              Recebeu uma indicação? Informe o código da parceira para vincular seu cadastro.
            </p>
          </div>
        )}
      </div>

      <p className="text-sm text-muted-foreground text-center">
        Ao me cadastrar, concordo com os{" "}
        <a href="/termos-de-uso" target="_blank" className="text-primary hover:underline font-medium">
          Termos de Uso
        </a>{" "}
        e com a{" "}
        <a href="/politica-de-privacidade" target="_blank" className="text-primary hover:underline font-medium">
          Política de Privacidade
        </a>
      </p>

      <div className="flex gap-3 pt-4">
        <Button
          variant="ghost"
          onClick={onBack}
          className="h-14 px-6 rounded-2xl text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <Button
          onClick={onContinue}
          disabled={!canContinue}
          className="flex-1 h-14 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base transition-all disabled:opacity-50"
        >
          Continuar
        </Button>
      </div>
    </motion.div>
  );
};

export default CompanyStep;
